import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Brain, Search, X } from 'lucide-react';

const GROUPS = [
  ['from_you', 'From you', 'Your own words. Use these first.'],
  ['perfect', 'Perfect', ''],
  ['near', 'Near', ''],
  ['multi', 'Multi', ''],
];

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (_error) {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(area);
    return copied;
  }
};

const RhymePanel = ({
  copied,
  error,
  loading,
  onClose,
  onPick,
  onQuickLookup,
  onSubmit,
  query,
  recent,
  result,
  setQuery,
  suffix,
}) => {
  const hasResults = GROUPS.some(([key]) => (result?.[key] || []).length);

  return (
    <div className="flex max-h-[78vh] flex-col overflow-hidden rounded-[1.75rem] border
      border-[color:var(--glow)]/50 bg-[#0b0d0e]/95 shadow-2xl backdrop-blur-xl"
    >
      <div className="flex items-start justify-between gap-3 border-b border-white/10 p-4">
        <div>
          <p className="flex items-center gap-2 font-heading text-xl font-black" style={{ color: 'var(--glow)' }}>
            <Brain className="h-5 w-5" /> Rhyme Brain
          </p>
          <p className="mt-1 text-base text-white/55">Find the word that lands.</p>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="flex min-h-11 min-w-11 items-center justify-center rounded-full text-white/60
              hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label="Close Rhyme Brain"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <div className="overflow-y-auto p-4">
        <form onSubmit={onSubmit}>
          <label htmlFor={`rhyme-search-${suffix}`} className="sr-only">Find rhymes</label>
          <div className="flex gap-2">
            <input
              id={`rhyme-search-${suffix}`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              maxLength={60}
              placeholder="Word or short phrase"
              className="min-h-12 min-w-0 flex-1 rounded-xl border border-white/15 bg-black/30 px-3
                text-base text-white outline-none placeholder:text-white/35 focus:border-[color:var(--glow)]"
            />
            <button
              type="submit"
              disabled={!query.trim() || loading}
              className="neon-btn flex min-h-12 min-w-12 items-center justify-center rounded-xl text-black
                disabled:opacity-45"
              style={{ backgroundColor: 'var(--glow)' }}
              aria-label="Look up rhymes"
            >
              <Search className="h-5 w-5" />
            </button>
          </div>
        </form>

        {recent.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" aria-label="Recent rhyme lookups">
            {recent.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => onQuickLookup(word)}
                className="min-h-11 rounded-full border border-white/10 px-3 text-base font-bold
                  text-white/65 hover:border-[color:var(--glow)] hover:text-white"
              >
                {word}
              </button>
            ))}
          </div>
        )}

        {loading && (
          <div className="mt-5 space-y-3" aria-label="Loading rhymes">
            {[1, 2, 3].map((row) => (
              <div key={row} className="h-11 animate-pulse rounded-xl bg-white/10" />
            ))}
          </div>
        )}

        {error && <p role="alert" className="mt-4 text-base font-bold text-[#FFB19C]">{error}</p>}

        {!loading && result && (
          <div className="mt-5">
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="font-heading text-2xl font-black">{result.word}</h3>
              <span className="text-base font-bold text-white/55">
                {result.syllables} {result.syllables === 1 ? 'syllable' : 'syllables'}
              </span>
            </div>
            {!result.known && (
              <p className="mt-2 text-base leading-relaxed text-white/65">
                Not in the book yet. Here's what matches the spelling.
              </p>
            )}
            {!hasResults && (
              <p className="mt-4 rounded-xl border border-white/10 p-3 text-base text-white/55">
                No matches yet. Try another word.
              </p>
            )}
            <div className="mt-4 space-y-5">
              {GROUPS.map(([key, label, caption]) => {
                const words = result[key] || [];
                if (!words.length) return null;
                return (
                  <section key={key}>
                    <h4 className="font-heading text-lg font-black" style={key === 'from_you'
                      ? { color: 'var(--glow)', textShadow: '0 0 12px var(--glow)' }
                      : undefined}
                    >
                      {label}
                    </h4>
                    {caption && <p className="mt-1 text-base text-white/55">{caption}</p>}
                    <div className="mt-2 flex flex-wrap gap-2">
                      {words.map((item) => (
                        <button
                          key={`${key}-${item.word}`}
                          type="button"
                          onClick={() => onPick(item.word)}
                          className="min-h-11 rounded-full border border-white/15 bg-white/[0.04]
                            px-3 text-base font-black hover:border-[color:var(--glow)]
                            hover:bg-white/[0.08] focus:outline-none focus-visible:ring-2
                            focus-visible:ring-[color:var(--glow)]"
                        >
                          {item.word} <span className="ml-1 text-sm text-white/45">{item.syllables}</span>
                        </button>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
            {copied && <p className="mt-4 text-base font-black" style={{ color: 'var(--glow)' }}>Copied</p>}
          </div>
        )}
      </div>
    </div>
  );
};

const RhymeBrain = ({ color, focusedLine, lookupTrigger, onFirstLookup, onSwap, requestLookup }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const firstLookup = useRef(false);
  const lastTrigger = useRef(null);

  const lookup = useCallback(async (value) => {
    const clean = String(value || '').trim();
    if (!clean) return;
    setQuery(clean);
    setLoading(true);
    setError('');
    setCopied(false);
    try {
      const data = await requestLookup(clean);
      setResult(data);
      setRecent((current) => [data.word, ...current.filter((word) => word !== data.word)].slice(0, 6));
      if (!firstLookup.current) {
        firstLookup.current = true;
        onFirstLookup();
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [onFirstLookup, requestLookup]);

  useEffect(() => {
    if (!lookupTrigger || lookupTrigger.id === lastTrigger.current) return;
    lastTrigger.current = lookupTrigger.id;
    setOpen(true);
    lookup(lookupTrigger.word);
  }, [lookup, lookupTrigger]);

  const submit = (event) => {
    event.preventDefault();
    lookup(query);
  };

  const pick = async (word) => {
    if (focusedLine) {
      onSwap(word);
      return;
    }
    if (await copyText(word)) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    }
  };

  const panelProps = {
    copied,
    error,
    loading,
    onPick: pick,
    onQuickLookup: lookup,
    onSubmit: submit,
    query,
    recent,
    result,
    setQuery,
  };

  return (
    <div style={{ '--glow': color }}>
      <aside className="sticky top-5 hidden lg:block">
        <RhymePanel {...panelProps} suffix="desktop" />
      </aside>

      <button
        type="button"
        onClick={() => setOpen(true)}
        className="neon-btn fixed bottom-4 right-4 z-30 flex min-h-12 items-center gap-2 rounded-full
          px-5 text-base font-black text-black lg:hidden"
        style={{ backgroundColor: color }}
      >
        <Brain className="h-5 w-5" /> Rhyme Brain
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/70 p-2 lg:hidden" role="dialog"
          aria-modal="true" aria-label="Rhyme Brain">
          <div className="w-full">
            <RhymePanel {...panelProps} suffix="mobile" onClose={() => setOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default RhymeBrain;
